import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { api, getApiError } from "@/lib/api";
import useDocumentTitle from "@/lib/useDocumentTitle";
import { toast } from "sonner";
import { ArrowRight, ArrowUpRight } from "lucide-react";

export default function Pricing() {
  useDocumentTitle(
    "Pricing — Azure Gypsy Guild Mortgage Relief | $1,500/yr + 1.5%",
    "Transparent two-fee model for Azure Gypsy Guild counterclaim mortgage relief: $1,500 yearly membership and 1.5% of mortgage balance (paid only at settlement). 98% settlement rate, 60-day timeline.",
  );
  const { user } = useAuth();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/content/landing").then((r) => setContent(r.data)).catch(() => {});
  }, []);

  const goCheckout = async (pkg) => {
    if (!user) { navigate("/login"); return; }
    try {
      setBusy(pkg);
      const { data } = await api.post("/payments/checkout", {
        package: pkg,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(getApiError(e));
      setBusy("");
    }
  };

  const fee = content?.membership_fee ?? 1500;
  const rate = content?.settlement_rate ?? 98;

  const membershipIncludes = [
    "Private members guild access",
    "Counterclaim filing + trustee oversight",
    "60-day case tracker",
    "Member portal & document vault",
    "All six e-sign agreements",
    "Marketplace · homes & land",
  ];
  const feeIncludes = [
    "Good accounting ledger audit",
    "Counterclaim filed with court, law firm & bank",
    "Foreclosure mediation + arbitration",
    "Trustee pledge & mortgage reassignment",
    "Manual tracking number on completion",
  ];

  return (
    <div className="min-h-screen bg-paper" data-testid="pricing-page">
      <Navbar />
      <main className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
        {/* Editorial header */}
        <div className="grid lg:grid-cols-12 gap-12 mb-24">
          <div className="lg:col-span-7">
            <div className="overline mb-6">§ Membership · Fee Schedule</div>
            <h1
              className="font-serif font-light text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tighter text-balance"
              data-testid="pricing-heading"
            >
              Two transparent fees.<br />
              <span className="font-editorial italic text-stone-500">One settlement.</span>
            </h1>
          </div>
          <div className="lg:col-span-4 lg:col-start-9 self-end">
            <p className="text-lg text-stone-600 font-light leading-relaxed">
              Backed by a <span className="text-stone-900 font-normal">{rate}% settlement rate</span>{" "}
              on completed cases. Nothing hidden. No renewal surprises.
            </p>
          </div>
        </div>

        {/* Fee ledger — two columns, hairline divided */}
        <div className="grid lg:grid-cols-2 border-t border-l border-stone-200">
          {/* Membership */}
          <div className="border-r border-b border-stone-200 bg-white p-10 md:p-14" data-testid="pricing-membership-card">
            <div className="caption text-stone-400 mb-8">01 · Yearly Membership</div>
            <div className="flex items-baseline gap-3">
              <span className="font-serif font-light text-7xl md:text-8xl leading-none tracking-tighter">
                ${fee.toLocaleString()}
              </span>
              <span className="caption text-stone-500">/year · auto-renew</span>
            </div>
            <ul className="mt-10 divide-y divide-stone-200">
              {membershipIncludes.map((f, i) => (
                <li key={f} className="py-4 flex items-baseline gap-4 text-[15px] text-stone-700 font-light">
                  <span className="caption text-stone-400 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  {f}
                </li>
              ))}
            </ul>
            <button
              onClick={() => goCheckout("membership")}
              disabled={busy === "membership"}
              className="btn-primary w-full justify-between mt-10 disabled:opacity-50"
              data-testid="pricing-membership-btn"
            >
              {busy === "membership" ? "Redirecting…" : "Pay membership"}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>

          {/* Service fee */}
          <div className="border-r border-b border-stone-200 bg-stone-50 p-10 md:p-14" data-testid="pricing-fee-card">
            <div className="caption text-stone-400 mb-8">02 · Counterclaim Service Fee</div>
            <div className="flex items-baseline gap-3">
              <span className="font-serif font-light text-7xl md:text-8xl leading-none tracking-tighter">
                1.5<span className="font-editorial italic text-stone-500">%</span>
              </span>
              <span className="caption text-stone-500">of mortgage balance</span>
            </div>
            <p className="text-sm text-stone-600 mt-6 leading-relaxed font-light max-w-md">
              Paid once per counterclaim, <span className="text-stone-900">only at settlement</span>.
              Calculated from the mortgage balance on file in your most recent application.
            </p>
            <ul className="mt-10 divide-y divide-stone-200">
              {feeIncludes.map((f, i) => (
                <li key={f} className="py-4 flex items-baseline gap-4 text-[15px] text-stone-700 font-light">
                  <span className="caption text-stone-400 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  {f}
                </li>
              ))}
            </ul>
            <button
              onClick={() => goCheckout("mortgage_fee")}
              disabled={busy === "mortgage_fee"}
              className="btn-outline w-full justify-between mt-10 disabled:opacity-50"
              data-testid="pricing-fee-btn"
            >
              {busy === "mortgage_fee" ? "Redirecting…" : "Pay 1.5% service fee"}
              <ArrowRight className="h-4 w-4" />
            </button>
            <p className="caption text-stone-500 mt-4">Requires application on file.</p>
          </div>
        </div>

        {/* Accounting explainer */}
        <section className="mt-24 md:mt-32" data-testid="pricing-accounting-explainer">
          <div className="border border-stone-200 bg-white p-10 md:p-14 grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <div className="overline mb-4">§ Why 1.5%?</div>
              <h2 className="font-serif font-light text-3xl md:text-4xl leading-tight tracking-tight text-stone-900">
                The service fee — explained line by line.
              </h2>
              <p className="mt-6 text-base md:text-lg text-stone-600 font-light leading-relaxed max-w-2xl">
                A single line on the trustee settlement statement, paid only at settlement.
                1.5% of your mortgage balance covers the ledger audit, trustee pledge, counterclaim
                filing, mediation/arbitration, and final mortgage reassignment.
                <span className="text-stone-900"> No settlement = no fee.</span>
              </p>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <Link
                to="/accounting"
                className="btn-primary inline-flex"
                data-testid="pricing-accounting-cta"
              >
                See the breakdown <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
